import { useQuery } from '@tanstack/react-query';
import Loading from '../shared/Loading';
import { getAuthHeader } from '../../helpers/authHelper';
import { showErrorAlert } from '../../helpers/alertHelper';

interface ShirtSizeSummaryItem {
    size: string;
    count: number;
}

interface ExportPersonModel {
    firstName: string;
    lastName: string;
    shirtSize: string;
}

interface ExportAttendeeModel {
    name: string;
    shirtSizes: string[];
    shirtSizesFormatted: string;
}

interface ExportData {
    shirtSizeSummary: ShirtSizeSummaryItem[];
    golfers: ExportPersonModel[];
    volunteers: ExportPersonModel[];
    attendees: ExportAttendeeModel[];
}

const fetchExportData = async (): Promise<ExportData> => {
    const resp = await fetch("/api/admin/get-export-data", {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader(),
        }
    });

    if (!resp.ok) {
        throw new Error("Failed to fetch export data");
    }

    return resp.json();
};

const Exports = () => {
    const { data, isLoading, isError } = useQuery<ExportData>({
        queryKey: ['exportData'],
        queryFn: fetchExportData,
    });

    if (isError) {
        showErrorAlert();
    }

    const getTimestamp = () => {
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    };

    const handleExport = async (endpoint: string, filename: string) => {
        try {
            const resp = await fetch(`/api/admin/${endpoint}`, {
                method: 'GET',
                headers: {
                    'Authorization': getAuthHeader(),
                }
            });

            if (resp.status !== 200) {
                showErrorAlert();
                return;
            }

            const blob = await resp.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const nameWithoutExt = filename.replace('.csv', '');
            a.download = `${nameWithoutExt}_${getTimestamp()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            showErrorAlert();
        }
    };

    return (
        <div className="container p-3">
            <h2 className="text-center">Exports</h2>

            {isLoading && <Loading />}

            {!isLoading && data && <>

                {/* Shirt Size Summary */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h3>Shirt Size Summary</h3>
                            <button
                                className="btn btn-success"
                                onClick={() => handleExport('export-shirt-summary', 'shirt-size-summary.csv')}>
                                <i className="bi bi-download me-1"></i> Export CSV
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-striped table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Size</th>
                                        <th>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.shirtSizeSummary.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.size}</td>
                                            <td>{item.count}</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td style={{ textAlign: 'right' }}><b>Total</b></td>
                                        <td><b>{data.shirtSizeSummary.reduce((acc, item) => acc + item.count, 0)}</b></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <hr />

                {/* Golfers */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h3>Golfers ({data.golfers.length})</h3>
                            <button
                                className="btn btn-success"
                                onClick={() => handleExport('export-golfers', 'golfers.csv')}>
                                <i className="bi bi-download me-1"></i> Export CSV
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-striped table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Last Name</th>
                                        <th>First Name</th>
                                        <th>Shirt Size</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.golfers.map((golfer, index) => (
                                        <tr key={index}>
                                            <td>{golfer.lastName}</td>
                                            <td>{golfer.firstName}</td>
                                            <td>{golfer.shirtSize}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <hr />

                {/* Volunteers */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h3>Volunteers ({data.volunteers.length})</h3>
                            <button
                                className="btn btn-success"
                                onClick={() => handleExport('export-volunteers', 'volunteers.csv')}>
                                <i className="bi bi-download me-1"></i> Export CSV
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-striped table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Last Name</th>
                                        <th>First Name</th>
                                        <th>Shirt Size</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.volunteers.map((volunteer, index) => (
                                        <tr key={index}>
                                            <td>{volunteer.lastName}</td>
                                            <td>{volunteer.firstName}</td>
                                            <td>{volunteer.shirtSize}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <hr />

                {/* Attendees */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h3>Attendees ({data.attendees.length})</h3>
                            <button
                                className="btn btn-success"
                                onClick={() => handleExport('export-attendees', 'attendees.csv')}>
                                <i className="bi bi-download me-1"></i> Export CSV
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-striped table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Shirt Sizes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.attendees.map((attendee, index) => (
                                        <tr key={index}>
                                            <td>{attendee.name}</td>
                                            <td>{attendee.shirtSizesFormatted}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

            </>}
        </div>
    );
}

export default Exports;
